import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import classNames from 'classnames';
import {makeStyles} from '@material-ui/core/styles';
import {Tabs, Tab, Box} from '@material-ui/core';
import {baseRoutes} from 'App/AppRouter/routes';
import {setContentItemViewTitles} from '../Redux/ContentItemViewActions';
import {selectGetSeries} from './Redux/ContentItemViewTitlesSelectors';
import './ContentItemViewTitles.scss';
import wrap from 'wraptile';

interface ItemEpisode {
    id: number;
    number: number;
    displayTitle: string;
    image: string;
}

interface ItemProp {
    id: number;
    number: number;
    episodes: ItemEpisode[];
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

export function TabPanel({children, value, index}: TabPanelProps) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            style={{
                minHeight: 'calc(100vh - 160px)',
                overflowY: 'scroll',
                width: '100%',
            }}
        >
            {value === index && <Box p={3}>{children}</Box>}
        </div>
    );
}

const useStyles = makeStyles(() => ({
    root: {
        flexGrow: 1,
        backgroundColor: '#2C2C2C',
        display: 'flex',
        height: 224,
    },
    tabs: {
        borderRight: `1px solid rgba(255, 255, 255, 0.12);`,
        color: '#ffffff',
    },
    tabPanel: {
        overflowY: 'scroll',
    },
    tab: {
        borderBottom: 'none',
    },
}));

export const tabPanelMapper = (value: number) => (item: ItemProp, index: number) => {
    return (
        <TabPanel value={value} key={item.id} index={index}>
            <div className="content-item-view-title-list">
                {item.episodes.map((episode: ItemEpisode) => {
                    return (
                        <div className="content-item-view-title-item" key={episode.id}>
                            <div className="content-item-view-title-item__img-wrap">
                                {episode.image ? (
                                    <img
                                        src={episode.image}
                                        className="content-item-view-title-item__img"
                                        alt={episode.displayTitle}
                                    />
                                ) : (
                                    <div className="content-item-view-title-item__img-absent">
                                        No artwork available
                                    </div>
                                )}
                            </div>
                            <a href={`${baseRoutes.contentItems}/${episode.id}`}>
                                <span className="content-item-view-title-item__desc">
                                    {episode.displayTitle}
                                </span>
                            </a>
                        </div>
                    );
                })}
            </div>
        </TabPanel>
    );
};

export const tabMapper = (item: ItemProp, index: number) => (
    <Tab
        label={`SEASON ${item.number}`}
        key={item.id}
        id={`tab-${index}`}
        style={{
            borderBottom: 'none',
        }}
        className="content-item-view-season-tab"
    />
);

export const changeHandler = wrap(
    (setValue: Function, event: React.ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
    }
);

export function ContentItemViewTitlesContainer({id}: {id: string}) {
    const classes = useStyles();
    const [value, setValue] = React.useState(0);
    const dispatch = useDispatch();
    const series = useSelector(selectGetSeries);

    const handleChange = changeHandler(setValue);

    useEffect(() => {
        dispatch(setContentItemViewTitles(id));
    }, []);

    return (
        <div className="content-item-view-title-wrap">
            <div className={classes.root}>
                <Tabs
                    orientation="vertical"
                    variant="scrollable"
                    value={value}
                    onChange={handleChange}
                    aria-label="Vertical tabs example"
                    className={classNames(classes.tabs, 'content-item-view-season-tab')}
                >
                    {series.map(tabMapper)}
                </Tabs>
                {series.map(tabPanelMapper(value))}
            </div>
        </div>
    );
}

export default ContentItemViewTitlesContainer;
